import { ITEMS_PER_PAGE, PAGE_SIZE_OPTIONS } from '@/constants';

/**
 * The page size a user picked for a list, remembered per list in this browser.
 *
 * It lives in localStorage rather than in the list store's persisted state:
 * init() resets every store through `$ResetPinia().all()`, and that reset clears
 * persisted state too, so the choice would not survive a reload. Stores read it
 * at request time for the same reason: a reset puts their `itemsPerPage` back to
 * the value from when the store was created.
 */
const STORAGE_KEY_PREFIX = 'milo.pageSize.v1.';

export function isPageSizeOption(value: unknown): value is number {
    return (PAGE_SIZE_OPTIONS as readonly unknown[]).includes(value);
}

export function readPageSize(list: string, fallback: number = ITEMS_PER_PAGE): number {
    try {
        const stored = Number(localStorage.getItem(STORAGE_KEY_PREFIX + list));
        return isPageSizeOption(stored) ? stored : fallback;
    } catch {
        return fallback;
    }
}

/** Remembers `size` for `list`. Returns false for a size that is not an option. */
export function storePageSize(list: string, size: number): boolean {
    if (!isPageSizeOption(size)) return false;

    try {
        localStorage.setItem(STORAGE_KEY_PREFIX + list, String(size));
    } catch {
        // Ignore storage failures (e.g. private mode); the size still applies until reload.
    }

    return true;
}
